import {Component, Injector, Input, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, FormGroupDirective, NgForm} from '@angular/forms';
import {Observable, Subject, Subscription} from 'rxjs';

// models
import {Country, OnboardingRequest, State, ValidationDetail} from '../../shared/model/models';

// services
import {FacadeService} from '../../shared/service-facade/facade.service';
import {SignupService} from "../service/signup.service";
import {NGXLogger} from "ngx-logger";
import {SectionStatusService} from "../../status-tracker/section-status.service";
import {StatusTrackerInterface} from "../../shared/interfaces/status-tracker.interface";
import {ReviewState} from "../../review/models/review-state";
import {AppValidator} from "../../shared/interfaces/AppValidator";
import {ErrorStateMatcher} from "@angular/material/core";
import {take, takeUntil} from "rxjs/operators";
import {AddressBehavior} from "./address-behavior";
import {MatRadioChange} from "@angular/material/radio";
import {TellUsAboutYourselfData} from "src/app/shared/model/form-data";
import {Options} from 'ngx-google-places-autocomplete/objects/options/options';
import {
  ADDRESS_VALIDATION_MESSAGE,
  EMAIL_APPLICANT_ERROR_KEY,
  EMAIL_FORM_CONTROL,
  PHONE_APPLICANT_ERROR_KEY,
  PHONE_FORM_CONTROL,
  SSN_APPLICANT_ERROR_KEY,
  SSN_FORM_CONTROL,
  SSN_VALIDATION_MESSAGE
} from "../../shared/constants";
import {ApplicantsService} from '../applicants/applicants.service';
import {Sections, SectionStates} from 'src/app/status-tracker/model/section-status';
import {EligibilityService} from "../../shared/services/eligibility.service";
import {AddressVerificationService} from 'src/app/core/services/address-verification.service';
import {SSNMaskingStrategy} from './strategies/ssn-masking.strategy';
import {SSNMaskingStrategyMap} from './strategies/ssn-masking.map';
import {ReviewService} from "../../review/services/review.service";
import {ProcessService} from "../../process/process.service";
import {ComponentNameEnum} from "../../shared/model/component-name.enum";

/** Error when invalid control is dirty, touched, or submitted. */
export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

@Component({
  selector: 'app-tell-us-about-yourself',
  templateUrl: './tell-us-about-yourself.component.html',
  styleUrls: ['./tell-us-about-yourself.component.scss']
})
export class TellUsAboutYourselfComponent extends AddressBehavior implements OnInit, StatusTrackerInterface, AppValidator, OnDestroy {
  @Input() isInJointMaintenanceFlow: boolean = false;
  @Input() reviewState: ReviewState = ReviewState.Initial;
  @Input() formGroup!: FormGroup;
  @Input() getPrimaryTellUsAboutYourselfData!: () => TellUsAboutYourselfData;
  suffix: string = "";
  stateList: State[] = [];
  hideSsn: boolean = true;
  maxDate = new Date();
  minDate = new Date();
  startDate = new Date();
  responseText: string = "";
  uspsSub!: Subscription;
  suggestedAddressSelection: number = 1;
  reviewStates = ReviewState;
  validationDetail: ValidationDetail;
  uneditedFormData: any;
  usePrimaryAddress: boolean = false;
  onboardingRequest: OnboardingRequest = new OnboardingRequest();

  secondaryInitialAddressLine1 : string = "";
  secondaryInitialAddressLine2 : string = "";
  secondaryInitialCity : string = "";
  secondaryInitialState : any = "";
  secondaryInitialZipCode : string = "";

  ssnMaskingStrategy!: SSNMaskingStrategy;
  ssnMask = "";
  isSSNHiddenInput = false;

  personalDetailMessage : string = "This should be your legal name and information as it appears on your goverment issued ID.";

  isEmployeeProduct: boolean = false;

  isInAuthenticatedFlow: boolean = false;

  isPWABrand$: Observable<boolean>;

  countryList: Country[] = [];

  options = {
    types: ['address'],
    componentRestrictions: { country: [ 'us'] }
  }  as unknown as Options;

  get isPrimary(): boolean {
    return this.formGroup.value.isPrimary;
  }

  get internationalCountries(): Country[] {
    return this.countryList.filter((country: Country) => country.isInternational);
  }

  matcher = new MyErrorStateMatcher();

  private _destroySubject$: Subject<void> = new Subject();
  private _formHasDuplicatedData = false;

  loading = false;
  isPWAProduct = false;
  isInReadOnlyState = false;

  emailNoAutofill = "email" + new Date().getTime().toString();
  ssnNoAutofill = "ssn" + new Date().getTime().toString();

  /**
   * Constructor
   * @param applicantService
   * @param facadeService
   * @param signupService
   * @param logger
   * @param addressVerificationService
   * @param statusTrackerService
   * @param eligibilityService
   * @param _injector
   * @param reviewService
   */
  constructor(
    private applicantService: ApplicantsService,
    private facadeService: FacadeService,
    private signupService: SignupService,
    private logger: NGXLogger,
    public statusTrackerService: SectionStatusService,
    private addressVerificationService: AddressVerificationService,
    private eligibilityService: EligibilityService,
    private _injector: Injector,
    private reviewService: ReviewService,
    private processService: ProcessService
  ) {
    super();

    this.validationDetail = new ValidationDetail(this.formGroup, new Map<string, string>());
    this.isPWABrand$ = this.facadeService.isPWABrand$;
  }

  /**
   * On Init
   */
  ngOnInit(): void {
    this.isInAuthenticatedFlow = this.facadeService.isInAuthenticatedFlow$.getValue()[0];

    this.countryList = this.facadeService.getCountries();

    this.isEmployeeProduct = this.facadeService.getIsEmployeeProduct();

    this.ssnMaskingStrategy = this._injector.get<SSNMaskingStrategy>(SSNMaskingStrategyMap.get(this.facadeService.getDeviceType()));
    this.ssnMask = this.ssnMaskingStrategy.getMask();
    this.isSSNHiddenInput = this.ssnMaskingStrategy.isHiddenInput();

    this.logger.debug("Starting tell us about yourself component...");
    this.stateList = this.facadeService.getAllStates();

    this.setupValidationMap();
    this.setPageDefaults();
    this.setupStatusTracker();
    this.setupValidationTrigger();
    this.validateSsnOnInitialLoad();

    this.bindFormControls(
      this.formGroup,
      this.formGroup.controls['city'],
      this.formGroup.controls['state'],
      this.formGroup.controls['zipCode']
    );

    this.onboardingRequest = this.facadeService.getShoppingCartOnboardingRequest();
    this.usePrimaryAddress = this.onboardingRequest.copyAddressFromPrimary ?? false;
    this.formGroup.valueChanges.pipe(takeUntil(this._destroySubject$)).subscribe(value => {
      this.signupService.formElementHasChanged = true;
    });

    this.applicantService.duplicatedDataErrors$.pipe(takeUntil(this._destroySubject$)).subscribe(dataErrors => {
      if (!dataErrors) {
        this._formHasDuplicatedData = false;
        this._removeDuplicateError(EMAIL_FORM_CONTROL);
        this._removeDuplicateError(PHONE_FORM_CONTROL);
        this._removeDuplicateError(SSN_FORM_CONTROL);
        return;
      }

      const duplicatedEmailError = dataErrors[EMAIL_APPLICANT_ERROR_KEY];
      const duplicatedPhoneError = dataErrors[PHONE_APPLICANT_ERROR_KEY];
      const duplicatedSSNError = dataErrors[SSN_APPLICANT_ERROR_KEY];

      if (duplicatedEmailError || duplicatedPhoneError || duplicatedSSNError) {
        this._formHasDuplicatedData = true;
      } else {
        this._formHasDuplicatedData = false;
      }

      if (duplicatedEmailError) {
        this.formGroup.controls[EMAIL_FORM_CONTROL].setErrors({"duplicated": true}, {emitEvent: false});
      }

      if (duplicatedPhoneError) {
        this.formGroup.controls[PHONE_FORM_CONTROL].setErrors({"duplicated": true}, {emitEvent: false});
      }

      if (duplicatedSSNError) {
        this.formGroup.controls[SSN_FORM_CONTROL].setErrors({"duplicated": true}, {emitEvent: false});
      }
    });

    if(this.facadeService.getIsMinor()) {
      if(this.formGroup.value.isPrimary) this.personalDetailMessage = "Primary applicant must be under the age of 18.";
      else this.personalDetailMessage = "Co-applicant must be over the age of 18 in order to serve as the custodian on this account. Please list your legal name as it appears on your government issued ID.";
    }

    this.isInReadOnlyState = this.reviewState === ReviewState.ReadOnly;
  }

  ngOnDestroy(): void {
    if (!this.formGroup.value.isPrimary) {
      this.clearValidations();
    }

    this._destroySubject$.next();
  }

  private _removeDuplicateError(controlName: string ): void {
    const control = this.formGroup.controls[controlName];
    const errors = control.errors;

    if (!errors) { return; }

    delete errors.duplicated;

    control.setErrors(errors, { emitEvent: false });
    control.updateValueAndValidity({ emitEvent: false });
  }

  getFieldError(key: string): any {
    if(this.formGroup.get(key)?.hasError('required') || key === "state"){
      return this.validationDetail.labels.get(key) + " is required.";
    }

    let control = this.formGroup.get(key);

    if(key === 'addressLine1' && control?.dirty && control.errors){
      return control.errors.message ?? ADDRESS_VALIDATION_MESSAGE;
    }

    if (key === SSN_FORM_CONTROL && control?.hasError('invalidSsnRange')) {
      return SSN_VALIDATION_MESSAGE;
    }

    if(key === 'firstName' || key === 'middleName' || key === 'lastName'){
      return this.validationDetail.labels.get(key) + " allows the special characters - . '";
    }

    return this.validationDetail.labels.get(key) + " is invalid.";
  }

  getFormControlValue(name: string): any {
    return this.formGroup.get(name)?.value;
  }

  getCountryOfCitizenshipDescription(): string {
    const { citizenship } = this.formGroup.value;

    if (!citizenship || citizenship === -1) {
      return "";
    }

    const countryId = Number(citizenship.toString().split("|")[0]);

    if (Number.isNaN(countryId)) {
      return citizenship;
    }

    return this.countryList.find(item => item.countryId === countryId)?.countryName || citizenship;
  }

  onUsePrimaryAddress(event: MatRadioChange): void {
    //onboardingRequest object is empty. so setting the values again.
    this.onboardingRequest = this.facadeService.getShoppingCartOnboardingRequest();
    this.onboardingRequest.copyAddressFromPrimary = this.usePrimaryAddress = event.value;

    if(this.usePrimaryAddress) {
      this.secondaryInitialAddressLine1 = this.formGroup.controls['addressLine1'].value;
      this.secondaryInitialAddressLine2 = this.formGroup.controls['addressLine2'].value;
      this.secondaryInitialCity = this.formGroup.controls['city'].value;
      this.secondaryInitialState = this.formGroup.controls['state'].value;
      this.secondaryInitialZipCode = this.formGroup.controls['zipCode'].value;
    }

    this.facadeService.setShoppingCartOnboardingRequest(this.onboardingRequest);
    const setFormValue = (name: string, value: any): void => {
      const control = this.formGroup.controls[name];
      control.setValue(value, {emitEvent: false});
      control.markAsTouched();
    }

    if(this.usePrimaryAddress) {
      var { addressLine1, addressLine2, city, state, zipCode } = this.getPrimaryTellUsAboutYourselfData();
    } else {
      var addressLine1 = this.secondaryInitialAddressLine1;
      var addressLine2 = this.secondaryInitialAddressLine2;
      var city = this.secondaryInitialCity;
      var state = this.secondaryInitialState;
      var zipCode = this.secondaryInitialZipCode;
    }

    setFormValue('addressLine1', addressLine1);
    setFormValue('addressLine2', addressLine2);
    setFormValue('city', city);
    setFormValue('state', state);
    setFormValue('zipCode', zipCode);
    this.formGroup.updateValueAndValidity();
  }

  compareValueWithSelection(optionValue: any, selectionValue: any): boolean {
    return optionValue === selectionValue;
  }

  setupValidationTrigger(): void {
    this.statusTrackerService.onTriggerValidations.pipe(takeUntil(this._destroySubject$)).subscribe(() => {
      this.formGroup.markAllAsTouched();
      this.formGroup.updateValueAndValidity();
    });
  };

  validateSsnOnInitialLoad(): any {
    const ssnControl = this.formGroup.get(SSN_FORM_CONTROL);
    if (!ssnControl) {
      return;
    }
    ssnControl.updateValueAndValidity({ emitEvent: false });
    if (this.isSsnValueInvalid(ssnControl)) {
      ssnControl.markAsTouched();
    }
  }

  isReadOnlySsnInvalid(): boolean {
    if (this.reviewState !== ReviewState.ReadOnly) {
      return false;
    }

    const ssnControl = this.formGroup.get(SSN_FORM_CONTROL);

    if (!ssnControl) {
      return false;
    }

    ssnControl.updateValueAndValidity({ emitEvent: false });

    return this.isSsnValueInvalid(ssnControl);
  }

  private isSsnValueInvalid(ssnControl: AbstractControl): boolean {
    const ssnValue = (ssnControl.value ?? '').toString().replace(/\D/g, '');

    if (!ssnValue) {
      return false;
    }

    return ssnValue.startsWith('9') || ssnControl.hasError('invalidSsnRange');
  }

  /**
   * Call to update validations in the validations component.
   */
  updateValidations() : void {
    this.signupService.validationsChange.emit(this.validationDetail);
  }

  clearValidations(): void {
    this.signupService.validationsChange.emit(
      new ValidationDetail(this.formGroup, this.validationDetail.labels, true)
    );
  }

  /**
   * Setup validation map for labels.
   */
  setupValidationMap(): void {
    // Setup labels for validation
    const { applicantType } = this.formGroup.value;
    let labelMap = new Map<string, string>();
    labelMap.set("firstName", "First Name");
    labelMap.set("middleName", "Middle Name");
    labelMap.set("lastName", "Last Name");
    labelMap.set("preferredName", "Preferred Name");
    labelMap.set("ssnNumber", "SSN");
    labelMap.set("birthDate", "Date of birth");
    labelMap.set("addressLine1", "Address Line 1");
    labelMap.set("addressLine2", "Address Line 2");
    labelMap.set("city", "City");
    labelMap.set("state", "State");
    labelMap.set("zipCode", "Zip Code");
    labelMap.set("phoneNumber", "Mobile number");

    if (this.shouldApplyCitizenshipValidation()) {
      labelMap.set("isUSCitizen", "U.S. Citizen");
      labelMap.set("citizenship", "Country of citizenship");
    }

    if(this.isEmployeeProduct){
      labelMap.set("emailAddress", "Personal Email address");
    }else {
      labelMap.set("emailAddress", "Email address");
    }

    this.validationDetail = new ValidationDetail(this.formGroup, labelMap);
  }

  private shouldApplyCitizenshipValidation(): boolean {
    // const isPWABrand = this.facadeService.getBrandId() === "3";
    return !this.isInAuthenticatedFlow;
  }

  touched(): void {
    if(this.formGroup.dirty) {
      this.formGroup.updateValueAndValidity();
    }
  }

  /***
   *  Get status changes for form and update status tracker.
   */
  setupStatusTracker(): void {
    this.formGroup.statusChanges.pipe(takeUntil(this._destroySubject$)).subscribe(state => {
      const isFormDirty = this.formGroup.dirty;
      this.logger.debug("Tell us about yourself, Status Change:" + state, " touched:" + isFormDirty);
      this.updateValidations();
    });
  }

  /**
   * Set page defaults
   */
  setPageDefaults(): void {
    this.suggestedAddressSelection = 2;

    // set the minimum on the birthday control
    let nowDate = new Date();

    if(this.formGroup.value.isPrimary && this.facadeService.getIsMinor()) {
      this.maxDate = nowDate;
      this.minDate = new Date(nowDate.getFullYear() - 18, nowDate.getMonth(), nowDate.getDate());
    } else {
      this.maxDate = new Date(nowDate.getFullYear() - 18, nowDate.getMonth(), nowDate.getDate());
      this.minDate = new Date(1900, 1, 1);
    }

    if (this.facadeService.getBrandId() === "3") {
      this.isPWAProduct = true;
    }
  }

  /**
   * Get the state name given an ID.
   * @param stateId
   */
  getStateAbbr(stateId: number | undefined): string {
    return this.stateList.find(state => state.id === stateId)?.stateAbbr || 'Not Found';
  }

  /**
   * Put the view in edit mode
   */
  editDetails() {
    this.uneditedFormData = this.formGroup.value;
    this.reviewState = ReviewState.Edit;

    this.reviewService.editStateArray.push([ComponentNameEnum.TellUsAboutYourself.toString(), this.isPrimary? 0 : 1])
    this.reviewService.editStateMapChanged.emit();

    this.isInReadOnlyState = false;
  }

  /**
   * Save the edited fields and set back to readonly
   */
  async save() {
    if (!this.formGroup.valid || this._formHasDuplicatedData) return;

    this.loading = true;
    this.processService.piHasChanged = true;

    let applicantId : number = 0;

    this.facadeService.getShoppingCartApplicationLogsRequest().applicants!.forEach(value => {
      if(this.formGroup.value.isPrimary === value.primary) { applicantId = value.applicantId!; }
    });

    // check applicant(s) eligibility
    try {
      if(applicantId > 0) {
        // Product eligibility needs to be called again, applicant personal information is linked
        // to product rules validation
        this.facadeService.getCurrentProductSelectionEligibility();

        this.addressVerificationService.verifyAddress(applicantId);

        this.addressVerificationService.addressVerificationComplete.pipe(
          take(1),
        ).subscribe(response => {
          if (response) {
            if (response.didAcceptSuggestion) {
              const address = response.address;

              const {addressLine1, addressLine2, city, state, zipCode} = this.formGroup.controls;

              addressLine1.setValue(address.addressLine1);
              addressLine2.setValue(address.addressLine2);
              city.setValue(address.city);
              state.setValue(address.stateId);
              zipCode.setValue(address.zip);
            }
          }

          this.reviewState = ReviewState.ReadOnly;
          this.isInReadOnlyState = true;

          const applicantIndex = this.isPrimary? 0 : 1;
          this.reviewService.editStateArray.splice(this.reviewService.editStateArray.findIndex(val => val[0] === ComponentNameEnum.TellUsAboutYourself.toString() && val[1] === applicantIndex),1)
          this.reviewService.editStateMapChanged.emit();
        });
      }
      this.loading = false;
    } catch(error) {
      this.loading = false
    }
  }

  /**
   * Cancel edit mode and restore fields to previous values.
   */
  cancel() {
    this.formGroup.patchValue(this.uneditedFormData, {emitEvent: false});
    this.formGroup.updateValueAndValidity();
    this.reviewState = ReviewState.ReadOnly;
    this._formHasDuplicatedData = false;

    const applicantIndex = this.isPrimary? 0 : 1;
    this.reviewService.editStateArray.splice(this.reviewService.editStateArray.findIndex(val => val[0] === ComponentNameEnum.TellUsAboutYourself.toString() && val[1] === applicantIndex),1)
    this.reviewService.editStateMapChanged.emit()

    this.isInReadOnlyState = true;
  }

  /* ====================================================================================================  */
  // Google Places/Maps
  /**
   * Fill addresses.
   * @param place
   */
  fillInAddress(place: any) {
    let address1: string = "";
    let suggestedCity: string = ""
    let postcode: string = "";
    let stateAbbr: string = "";

    this.logger.log("Place = " + JSON.stringify(place));

    // Get each component of the address from the place details,
    // and then fill-in the corresponding field on the form.
    // place.address_components are google.maps.GeocoderAddressComponent objects
    // which are documented at http://goo.gle/3l5i5Mr
    for (const component of place.address_components as google.maps.GeocoderAddressComponent[]) {
      // @ts-ignore remove once typings fixed
      const componentType = component.types[0];

      switch (componentType) {
        case "street_number": {
          address1 = `${component.long_name} ${address1}`;
          break;
        }

        case "route": {
          address1 += component.short_name;
          break;
        }

        case "postal_code": {
          postcode = `${component.long_name}${postcode}`;
          break;
        }

        case "postal_code_suffix": {
          postcode = `${postcode}-${component.long_name}`;
          break;
        }

        case "locality":
          suggestedCity = component.long_name;
          break;

        case "administrative_area_level_1": {
          stateAbbr = component.short_name;
          break;
        }

        case "country":
          break;
      }
    }

    // Bug 398139: some addresses not populating city information
    // If we couldn't pull the city info from the locality array, pull from the vicinity
    if (!suggestedCity) suggestedCity = place.vicinity ?? "";

    const { addressLine1, addressLine2, city, state, zipCode } = this.formGroup.controls;
    // clear out values in address line 2 - not part of the returned data set
    addressLine2.setValue("");

    // set the form control values based on the address returned
    addressLine1.setValue(address1);
    zipCode.setValue(postcode);
    city.setValue(suggestedCity);
    state.setValue((this.stateList.find(state => state.stateAbbr === stateAbbr))?.id);

    this.formGroup.updateValueAndValidity();
  }

  //function to include slashes automatically
  onDateKeyUp(event: KeyboardEvent) {
    let element = event.currentTarget as HTMLInputElement
    const value = element.value;
    if (event.keyCode != 8) { //skip backspace event
      if (value.length == 2) {
        element.value = value + '/';
      } else if (value.length == 5) {
        element.value = value + '/';
      }
    }
  }

  ssnFieldMaskOnBlur() {
    if(!this.hideSsn) this.hideSsn = true;
  }

  handleSSNkeypress(event: Event) {
    event.preventDefault();
    this.hideSsn = false;
    const ssnField = document.getElementById('ssnNumber') as HTMLElement;
    if(ssnField) {
      ssnField.focus();
    }
  }

  formatBirthDate(val: unknown): string {
    if (!val) return '';
    if (typeof val === 'string') return val;
    if (val instanceof Date) {
      const mm = String(val.getMonth() + 1).padStart(2, '0');
      const dd = String(val.getDate()).padStart(2, '0');
      const yyyy = val.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    }
    return '';
  }
  
}
